/**
 * CheckoutForm.js — Ship / Pickup / Payment steps (kind "checkout-form"),
 * Potter's Vineyard variant.
 *
 * Ported from Lightning Rock Winery's CheckoutForm (same Commerce7
 * "reviewed" checkout flow — see checkout_c7.js), adapted for a US winery:
 * default country US (not CA), ZIP code (not Canadian postal code)
 * validation, with the production "Province" label.
 *   shippingForm    → "ship" — First Name, Last Name, Company (optional),
 *                      Email, Country, Province, City, Address,
 *                      ZIP Code, Phone (one "Shipping address" card).
 *   pickupForm      → "pickup" — First/Last Name, Company, Email (guest
 *                      only), Country, + pickup location list.
 *   paymentOptions / chargeStepForm → "payment" — saved cards (radio list,
 *                      default badge, "Use a new card") or new-card fields
 *                      (Card Holder Name, Card Number, Expiry Month/Year,
 *                      CVV, "Make my default card" when editing).
 *
 * Country/state from api.c7GetShippingRegions (Commerce7 curated regions —
 * old getC7Countries), filtered to the selected country (default US).
 * Saved cards from api.c7GetSavedCards (old getSavedCards).
 *
 * Ship/Pickup emit ws-checkout-submit { type, fields }. Payment (no saved
 * card picked / guest / new card) emits ws-checkout-charge { checkoutType,
 * cardId? | cardData? } — the Commerce7 charge event checkout_c7.js already
 * listens for (old submitNewCreditCard / chargeStepForm submit handlers
 * posted raw card fields straight to the BE with no client-side
 * tokenization; this keeps that same trade-off — flag it if PCI scope is a
 * concern before this ships).
 *
 * Editing one specific saved card (pencil icon in the saved-card list, old
 * paymentOptions' edit-action-btn → chargeStepForm(true, card, ...)):
 * ws-checkout-card-edit { card } opens this dedicated card-detail form
 * prefilled from that card (no picker shown alongside it); submitting it
 * emits ws-checkout-card-update { cardId, payload } — an update to that
 * card's details (old updateCardFormButton), not a charge.
 *
 * Listens for ws-checkout-error to re-enable Continue (old forms stayed
 * editable when the BE returned field errors).
 *
 * message.data: { type: "ship"|"pickup"|"payment", values?: {...prefill for Edit} }
 */

import { on, emit, getState } from "../../../core/state.js";
import * as api from "../../../core/api.js";

function isValidUSZip(zip) {
  const normalized = String(zip || "").trim();
  return /^\d{5}(-\d{4})?$/.test(normalized);
}

function cleanField(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

// Show/hide toggle icons for masked fields (card number / CVV) — same
// stroke-icon style as OrderReview's EDIT_ICON, so it reads as one system
// instead of mixing in an emoji.
const EYE_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/></svg>`;
const EYE_OFF_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24M10.73 5.08A10.94 10.94 0 0 1 12 5c7 0 11 7 11 7a18.5 18.5 0 0 1-4.16 4.94M6.61 6.61A18.5 18.5 0 0 0 1 12s4 7 11 7a10.94 10.94 0 0 0 5.39-1.61" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

// Same icon as OrderReview's "Edit" — reused here so the saved-card pencil
// reads as one system.
const EDIT_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>`;

function fieldGroup({
  name,
  label,
  placeholder,
  required = false,
  select = null,
  type = null,
  mask = false,
  values = {},
  refs,
  countrySel,
  provinceSel,
}) {
  const group = document.createElement("div");
  group.className = "ws-checkout-field";

  if (label) {
    const lab = document.createElement("label");
    // Old shippingForm: "Company <span>(optional)</span>"
    if (/\(optional\)\s*$/i.test(label)) {
      const base = label.replace(/\s*\(optional\)\s*$/i, "").trim();
      lab.appendChild(document.createTextNode(`${base} `));
      const opt = document.createElement("span");
      opt.textContent = "(optional)";
      lab.appendChild(opt);
    } else {
      lab.textContent = label;
    }
    group.appendChild(lab);
  }

  let input;
  if (select) {
    input = document.createElement("select");
    if (select === "country") countrySel.el = input;
    if (select === "province") provinceSel.el = input;
  } else {
    input = document.createElement("input");
    input.type = type || (name === "phone" ? "tel" : "text");
    if (placeholder) input.placeholder = placeholder;
  }
  input.name = name;
  input.className = "ws-checkout-input";
  const prefill = cleanField(values[name]);
  if (prefill) input.value = prefill;

  // Card number: digits only, capped at 16 (standard card length).
  if (name === "cardNumber") {
    input.inputMode = "numeric";
    input.maxLength = 16;
    input.addEventListener("input", () => {
      input.value = input.value.replace(/\D/g, "").slice(0, 16);
    });
  }

  // Old chargeStepForm: masked card-number input with a show/hide toggle.
  if (mask) {
    input.style.paddingRight = "36px";
    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.appendChild(input);
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "ws-checkout-mask-toggle";
    toggle.innerHTML = EYE_OFF_ICON;
    toggle.setAttribute("aria-label", "Show card details");
    toggle.style.position = "absolute";
    toggle.style.right = "8px";
    toggle.style.top = "50%";
    toggle.style.transform = "translateY(-50%)";
    toggle.style.border = "none";
    toggle.style.background = "transparent";
    toggle.style.color = "#737373";
    toggle.style.display = "inline-flex";
    toggle.style.padding = "0";
    toggle.style.cursor = "pointer";
    toggle.addEventListener("click", () => {
      const hidden = input.type === "password";
      input.type = hidden ? "text" : "password";
      toggle.innerHTML = hidden ? EYE_ICON : EYE_OFF_ICON;
      toggle.setAttribute(
        "aria-label",
        hidden ? "Hide card details" : "Show card details",
      );
    });
    wrap.appendChild(toggle);
    group.appendChild(wrap);
  } else {
    group.appendChild(input);
  }

  const err = document.createElement("small");
  err.className = "ws-checkout-error";
  group.appendChild(err);

  refs[name] = { input, err, required: !!required };
  return group;
}

function section(titleText, subtitleText) {
  const sec = document.createElement("div");
  sec.className = "ws-checkout-section";
  const head = document.createElement("div");
  head.className = "ws-checkout-section-head";
  const title = document.createElement("p");
  title.className = "ws-checkout-section-title";
  title.textContent = titleText;
  head.appendChild(title);
  if (subtitleText) {
    const subtitle = document.createElement("p");
    subtitle.style.margin = "0 0 4px";
    subtitle.style.fontSize = "13px";
    subtitle.style.color = "#737373";
    subtitle.textContent = subtitleText;
    head.appendChild(subtitle);
  }
  sec.appendChild(head);
  const fields = document.createElement("div");
  fields.className = "ws-checkout-fields";
  sec.appendChild(fields);
  return { sec, fields };
}

function row(cols = 2) {
  const el = document.createElement("div");
  el.className = `ws-checkout-row ws-checkout-row--${cols}`;
  return el;
}

export default class CheckoutForm {
  render(message) {
    const dataType = message?.data?.type;
    const type =
      dataType === "pickup" || dataType === "payment" ? dataType : "ship";
    const values = message?.data?.values || {};
    // Old pickupForm(false, { firstName, lastName }) still has prefilled
    // names — isEdit is a separate flag, not "any values present".
    const isEdit = message?.data?.isEdit === true;

    const form = document.createElement("form");
    form.className = "ws-checkout-form";
    if (message?.id) form.dataset.pvCheckoutId = message.id;
    if (type === "ship") form.classList.add("ws-pv-shipping-form");
    form.noValidate = true;

    const refs = {};
    const countrySel = { el: null };
    const provinceSel = { el: null };
    const paymentSel = { cardId: null };
    const ctx = { values, refs, countrySel, provinceSel };
    // The submit-error line + footer button render inside this section's
    // card (not as siblings of it) — only one of ship/pickup/payment ever
    // builds per render.
    let activeSec = null;

    if (type === "ship") {
      const shipping = section(
        "Shipping address",
        isEdit ? "Edit shipping details" : "Add shipping details",
      );

      const nameRow = row(1);
      nameRow.appendChild(
        fieldGroup({
          name: "firstName",
          label: "First Name",
          placeholder: "Enter your first name",
          required: true,
          ...ctx,
        }),
      );
      nameRow.appendChild(
        fieldGroup({
          name: "lastName",
          label: "Last Name",
          placeholder: "Enter your last name",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(nameRow);

      const companyRow = row(1);
      companyRow.appendChild(
        fieldGroup({
          name: "company",
          label: "Company (optional)",
          placeholder: "Enter company name",
          ...ctx,
        }),
      );
      shipping.fields.appendChild(companyRow);

      const emailRow = row(1);
      emailRow.appendChild(
        fieldGroup({
          name: "email",
          label: "Email",
          placeholder: "Enter email id",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(emailRow);

      const countryRow = row(1);
      countryRow.appendChild(
        fieldGroup({
          name: "country",
          label: "Country",
          required: true,
          select: "country",
          ...ctx,
        }),
      );
      shipping.fields.appendChild(countryRow);

      const stateRow = row(1);
      stateRow.classList.add("ws-checkout-province-row");
      stateRow.appendChild(
        fieldGroup({
          name: "province",
          label: "Province",
          required: true,
          select: "province",
          ...ctx,
        }),
      );
      shipping.fields.appendChild(stateRow);

      const cityRow = row(1);
      cityRow.appendChild(
        fieldGroup({
          name: "city",
          label: "City",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(cityRow);

      const addrRow = row(1);
      addrRow.appendChild(
        fieldGroup({
          name: "address1",
          label: "Address",
          placeholder: "Enter Address",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(addrRow);

      const zipRow = row(1);
      zipRow.appendChild(
        fieldGroup({
          name: "zip",
          label: "ZIP Code",
          placeholder: "Enter ZIP Code",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(zipRow);

      const phoneRow = row(1);
      phoneRow.appendChild(
        fieldGroup({
          name: "phone",
          label: "Phone (e.g. +12025550100)",
          placeholder: "+12025550100",
          required: true,
          ...ctx,
        }),
      );
      shipping.fields.appendChild(phoneRow);

      form.appendChild(shipping.sec);
      activeSec = shipping.sec;

      this._loadCountries(countrySel.el, provinceSel.el, values, stateRow, refs);
    }

    if (type === "pickup") {
      // Old customer_token — Commerce7 login stores customerToken, not
      // isAuthenticated (that flag was never set on C7 resolve).
      const loggedIn = !!(
        getState().user?.customerToken || getState().user?.isAuthenticated
      );
      const pickup = section(
        "Pickup Contact",
        isEdit ? "Edit order details" : "",
      );

      const nameRow = row(2);
      nameRow.appendChild(
        fieldGroup({
          name: "firstName",
          label: "First Name",
          placeholder: "John",
          required: true,
          ...ctx,
        }),
      );
      nameRow.appendChild(
        fieldGroup({
          name: "lastName",
          label: "Last Name",
          placeholder: "Doe",
          required: true,
          ...ctx,
        }),
      );
      pickup.fields.appendChild(nameRow);

      const companyRow = row(1);
      companyRow.appendChild(
        fieldGroup({
          name: "company",
          label: "Company (optional)",
          placeholder: "Enter company name",
          ...ctx,
        }),
      );
      pickup.fields.appendChild(companyRow);

      // Old pickupForm: email only for guests — logged-in customers skip it.
      if (!loggedIn) {
        const emailRow = row(1);
        emailRow.appendChild(
          fieldGroup({
            name: "email",
            label: "Email",
            placeholder: "johndoe@gmail.com",
            required: true,
            ...ctx,
          }),
        );
        pickup.fields.appendChild(emailRow);
      }

      const countryRow = row(1);
      countryRow.appendChild(
        fieldGroup({
          name: "country",
          label: "Country",
          required: true,
          select: "country",
          ...ctx,
        }),
      );
      pickup.fields.appendChild(countryRow);

      const locationField = document.createElement("div");
      locationField.className = "ws-checkout-field";
      const locationLabel = document.createElement("div");
      locationLabel.style.paddingBottom = "7px";
      locationLabel.style.color = "#000";
      locationLabel.style.fontWeight = "600";
      locationLabel.textContent = "Pickup location";
      locationField.appendChild(locationLabel);
      const locationList = document.createElement("div");
      locationField.appendChild(locationList);
      pickup.fields.appendChild(locationField);

      form.appendChild(pickup.sec);
      activeSec = pickup.sec;

      this._loadCountries(countrySel.el, null, values, null, refs);
      this._loadPickupLocations(locationList);
    }

    if (type === "payment") {
      const payment = section("Payment", isEdit ? "Edit card details" : "");

      const savedCardWrap = document.createElement("div");
      payment.fields.appendChild(savedCardWrap);

      const newCardWrap = document.createElement("div");
      newCardWrap.className = "ws-checkout-fields";
      // Breathing room from the saved-card list above it when both are
      // visible at once (clicking "+ Use a new card" reveals these fields
      // inline, right below the list, rather than hiding it).
      newCardWrap.style.marginTop = "16px";
      // Editing one specific saved card (pencil icon in the saved-card list)
      // always shows the card-detail fields directly — old chargeStepForm(true,
      // card, ...) never re-shows the picker alongside it. Otherwise hide by
      // default for a logged-in customer until we know whether they have
      // saved cards (old paymentOptions only builds one form or the other) —
      // guests never have saved cards, so skip the flash for them.
      newCardWrap.hidden = isEdit ? false : !!getState().user?.customerId;

      const holderRow = row(1);
      holderRow.appendChild(
        fieldGroup({
          name: "cardHolderName",
          label: "Card Holder Name",
          placeholder: "John Doe",
          required: true,
          ...ctx,
        }),
      );
      newCardWrap.appendChild(holderRow);

      const numberRow = row(1);
      numberRow.appendChild(
        fieldGroup({
          name: "cardNumber",
          label: "Card Number",
          placeholder: "**** **** **** ****",
          required: true,
          type: "password",
          mask: true,
          ...ctx,
        }),
      );
      newCardWrap.appendChild(numberRow);

      const expiryRow = row(2);
      expiryRow.appendChild(
        fieldGroup({
          name: "expiryMo",
          label: "Expiry Month",
          placeholder: "MM",
          required: true,
          ...ctx,
        }),
      );
      expiryRow.appendChild(
        fieldGroup({
          name: "expiryYr",
          label: "Expiry Year",
          placeholder: "YYYY",
          required: true,
          ...ctx,
        }),
      );
      newCardWrap.appendChild(expiryRow);

      const cvvRow = row(1);
      cvvRow.appendChild(
        fieldGroup({
          name: "cvv2",
          label: "CVV",
          placeholder: "XXX",
          required: true,
          type: "password",
          mask: true,
          ...ctx,
        }),
      );
      newCardWrap.appendChild(cvvRow);

      // Old chargeStepForm: "Make my default card" only shown when editing.
      if (isEdit) {
        const defaultRow = row(1);
        const defaultGroup = document.createElement("div");
        defaultGroup.className = "ws-checkout-field";
        const defaultLabel = document.createElement("label");
        const isDefaultCheckbox = document.createElement("input");
        isDefaultCheckbox.type = "checkbox";
        isDefaultCheckbox.name = "isDefault";
        defaultLabel.appendChild(isDefaultCheckbox);
        defaultLabel.appendChild(
          document.createTextNode(" Make my default card"),
        );
        defaultGroup.appendChild(defaultLabel);
        defaultRow.appendChild(defaultGroup);
        newCardWrap.appendChild(defaultRow);
      }

      payment.fields.appendChild(newCardWrap);
      form.appendChild(payment.sec);
      activeSec = payment.sec;

      // Skip the saved-card picker when editing one specific card — old
      // chargeStepForm(true, card, ...) is a dedicated single-card form, it
      // never shows the list alongside it (that's paymentOptions' job).
      if (!isEdit) {
        this._loadSavedCards(savedCardWrap, newCardWrap, paymentSel);
      }
    }

    // Old submit-error (shippingForm/pickupForm/chargeStepForm): a form-level
    // error line above the footer for non-field failures (e.g. the BE/C7
    // request itself failing), separate from the per-field <small> errors.
    const formError = document.createElement("small");
    formError.className = "ws-checkout-error ws-checkout-submit-error";
    (activeSec || form).appendChild(formError);

    const footer = document.createElement("div");
    footer.className = "ws-checkout-footer";
    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "ws-checkout-submit";
    // Old shippingForm: Update when editing. Old pickupForm/chargeStepForm:
    // Continue/Place Order (chargeStepForm: Update Card only when editing).
    submit.textContent =
      type === "ship" && isEdit
        ? "Update"
        : type === "payment"
          ? isEdit
            ? "Update Card"
            : "Place Order"
          : "Continue...";
    footer.appendChild(submit);
    (activeSec || form).appendChild(footer);

    let pending = false;

    form.addEventListener("submit", (e) => {
      e.preventDefault();

      // Old paymentOptions: a selected saved card skips the new-card fields
      // entirely and charges that card directly.
      if (type === "payment" && paymentSel.cardId) {
        pending = true;
        submit.disabled = true;
        emit("ws-checkout-charge", {
          checkoutType: "saved_card",
          cardId: paymentSel.cardId,
        });
        return;
      }

      const { ok, data } = this._validate(refs);
      // Keep Continue enabled when client validation fails (old form never
      // disabled the button on required-field errors).
      if (!ok) {
        pending = false;
        submit.disabled = false;
        return;
      }
      pending = true;
      submit.disabled = true;

      if (type === "payment") {
        data.cardNumber = (data.cardNumber || "").replace(/\s/g, "");
        data.isDefault = !!form.querySelector('input[name="isDefault"]')
          ?.checked;
        // Editing one specific saved card (old updateCardFormButton) — an
        // update to that card's details, not a charge.
        if (isEdit) {
          emit("ws-checkout-card-update", { cardId: values.id, payload: data });
          return;
        }
        emit("ws-checkout-charge", {
          checkoutType: getState().user?.customerId ? "new_card" : "guest",
          cardData: data,
        });
        return;
      }

      emit("ws-checkout-submit", { type, fields: data });
    });

    // API / BE failure — re-enable Continue and show errors on the form
    // itself (old parity: per-field <small> errors, or the submit-error line
    // for a non-field failure like the BE/C7 request failing outright).
    on("ws-checkout-error", ({ fieldErrors, message } = {}) => {
      if (!form.isConnected || !pending) return;
      pending = false;
      submit.disabled = false;
      formError.textContent = "";
      if (fieldErrors && typeof fieldErrors === "object" && Object.keys(fieldErrors).length) {
        Object.entries(fieldErrors).forEach(([name, msg]) => {
          const ref = refs[name];
          if (ref?.err) ref.err.textContent = String(msg || "");
        });
      } else if (message) {
        formError.textContent = String(message);
      }
    });

    // Success — keep disabled like old fieldset.disabled after showOrderDetails.
    on("ws-checkout-success", () => {
      if (!form.isConnected || !pending) return;
      pending = false;
      submit.disabled = true;
      form.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = true;
      });
    });

    return form;
  }

  async _loadCountries(countryEl, provinceEl, values, stateRow, refs) {
    let regions = [];
    try {
      const res = await api.c7GetShippingRegions();
      regions = Array.isArray(res) ? res : [];
    } catch {
      /* leave selects empty */
    }
    if (!countryEl) return;

    // Old getC7Countries: filter the curated region list down to just the
    // selected country (default US) — Potter's Vineyard doesn't offer a free
    // country choice, the select just reflects the one country in play.
    const selectedCountry = cleanField(values.country) || "US";
    const countries = regions.filter((c) => c.countryCode === selectedCountry);
    if (!countries.length) return;

    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = "Select Country";
    ph.disabled = true;
    countryEl.appendChild(ph);

    countries.forEach((c) => {
      const opt = document.createElement("option");
      opt.value = c.countryCode;
      opt.textContent = c.country;
      opt.selected = true;
      countryEl.appendChild(opt);
    });
    countryEl.value = selectedCountry;

    const fillProvinces = (countryCode, selected) => {
      if (!provinceEl) return;
      provinceEl.replaceChildren();
      const country = countries.find((c) => c.countryCode === countryCode);
      const states = country?.states || [];

      // Old shippingForm always shows State as required; only hide it for
      // the rare country with no state/province list.
      if (stateRow) stateRow.hidden = states.length === 0;
      if (refs.province) refs.province.required = states.length > 0;

      const p0 = document.createElement("option");
      p0.value = "";
      p0.textContent = "Select State";
      p0.disabled = true;
      p0.selected = !selected;
      provinceEl.appendChild(p0);

      states.forEach((s) => {
        const opt = document.createElement("option");
        opt.value = s.stateCode;
        opt.textContent = s.stateName;
        if (selected && selected === s.stateCode) opt.selected = true;
        provinceEl.appendChild(opt);
      });
    };

    countryEl.addEventListener("change", () =>
      fillProvinces(countryEl.value, null),
    );
    fillProvinces(selectedCountry, values.province);
  }

  async _loadPickupLocations(container) {
    let locations = [];
    try {
      const res = await api.c7GetPickupLocations();
      locations = Array.isArray(res?.pickupLocations)
        ? res.pickupLocations
        : Array.isArray(res)
          ? res
          : [];
    } catch {
      /* leave list empty */
    }

    container.replaceChildren();
    if (!locations.length) {
      const none = document.createElement("p");
      none.textContent = "No pickup locations available.";
      container.appendChild(none);
      return;
    }

    locations.forEach((loc) => {
      const card = document.createElement("p");
      card.style.margin = "0 0 8px";

      const title = document.createElement("strong");
      title.textContent = loc.title || "";
      card.appendChild(title);

      const addrLine = [loc.address, loc.address2].filter(Boolean).join(", ");
      const cityLine = [loc.stateCode, loc.city].filter(Boolean).join(", ");
      const countryLine = [loc.countryCode, loc.zipCode]
        .filter(Boolean)
        .join("-");
      [addrLine, cityLine, countryLine, loc.phone ? `Phone: ${loc.phone}` : ""]
        .filter(Boolean)
        .forEach((text) => {
          card.appendChild(document.createElement("br"));
          card.appendChild(document.createTextNode(text));
        });

      container.appendChild(card);
    });
  }

  // Old paymentOptions: saved-card radio list (default first, "Use another
  // card" for the rest) + a toggle to the new-card fields. Falls through to
  // the new-card form untouched when the customer has no saved cards, or
  // isn't logged in at all (guest checkout, old chargeStepForm default path).
  async _loadSavedCards(container, newCardWrap, paymentSel) {
    const customerId = getState().user?.customerId || "";
    let savedCards = [];
    if (customerId) {
      try {
        const res = await api.c7GetSavedCards(customerId);
        savedCards = Array.isArray(res?.savedCards)
          ? res.savedCards
          : Array.isArray(res)
            ? res
            : [];
      } catch {
        /* fall through to the new-card form */
      }
    }

    if (!savedCards.length) {
      newCardWrap.hidden = false;
      return;
    }

    const list = document.createElement("div");
    list.className = "ws-checkout-fields";

    const renderRow = (card) => {
      const wrap = document.createElement("label");
      wrap.className = "ws-checkout-field";
      wrap.style.display = "flex";
      wrap.style.flexDirection = "row";
      wrap.style.alignItems = "center";
      wrap.style.gap = "10px";
      wrap.style.cursor = "pointer";

      const radio = document.createElement("input");
      radio.type = "radio";
      radio.name = "savedCard";
      radio.value = card.id;
      radio.checked = !!card.isDefault;
      radio.addEventListener("change", () => {
        paymentSel.cardId = card.id;
        newCardWrap.hidden = true;
      });
      wrap.appendChild(radio);

      const info = document.createElement("div");
      info.style.flex = "1 1 auto";
      const brand = document.createElement("div");
      brand.style.fontWeight = "600";
      brand.textContent = `${card.cardBrand || "Card"}${card.isDefault ? " (Default)" : ""}`;
      info.appendChild(brand);
      const details = document.createElement("div");
      details.style.fontSize = "12px";
      details.textContent = `${card.maskedCardNumber || ""} · Expiry: ${card.expiryMo}/${card.expiryYr}`;
      info.appendChild(details);
      wrap.appendChild(info);

      // Pencil (old paymentOptions edit-action-btn) → open this specific
      // card's edit-details form (chargeStepForm(true, card, ...)), not the
      // saved-card picker. Stop it from also toggling the radio (a click
      // anywhere else in the label selects this card).
      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "ws-order-edit";
      editBtn.setAttribute("aria-label", "Edit card");
      editBtn.innerHTML = EDIT_ICON;
      editBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        emit("ws-checkout-card-edit", { card });
      });
      wrap.appendChild(editBtn);

      return wrap;
    };

    savedCards.filter((c) => c.isDefault).forEach((c) => list.appendChild(renderRow(c)));
    savedCards.filter((c) => !c.isDefault).forEach((c) => list.appendChild(renderRow(c)));
    container.appendChild(list);

    const useNew = document.createElement("button");
    useNew.type = "button";
    useNew.className = "ws-checkout-submit";
    useNew.style.background = "transparent";
    useNew.style.color = "inherit";
    useNew.style.border = "1px solid #e5e5e5";
    useNew.textContent = "+ Use a new card";
    useNew.addEventListener("click", () => {
      paymentSel.cardId = null;
      list
        .querySelectorAll('input[type="radio"]')
        .forEach((r) => (r.checked = false));
      newCardWrap.hidden = false;
    });
    container.appendChild(useNew);

    const defaultCard = savedCards.find((c) => c.isDefault) || savedCards[0];
    paymentSel.cardId = defaultCard.id;
    newCardWrap.hidden = true;
  }

  _validate(refs) {
    let ok = true;
    const data = {};
    Object.entries(refs).forEach(([name, ref]) => {
      const { input, err, required } = ref;
      const value = cleanField(input.value);
      data[name] = value;
      // Skip required-checks for anything hidden (a collapsed province row,
      // or the new-card fields when a saved card is selected instead).
      const hidden = !!input.closest("[hidden]");
      if (required && !hidden && !value) {
        err.textContent = "This field is required";
        ok = false;
      } else if (name === "zip" && value && !isValidUSZip(value)) {
        err.textContent = "Please enter a valid ZIP code.";
        ok = false;
      } else {
        err.textContent = "";
      }
    });
    return { ok, data };
  }
}
